import { useDraggable, useDroppable } from '@dnd-kit/core'
import { type BoardCard, type BoardColumn, boardColumns } from '@hugo/core'
import { BellRingIcon, EllipsisIcon, HistoryIcon, MegaphoneIcon } from 'lucide-react'
import { type ReactElement, useCallback } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { type CardLine, COLUMN_LABELS, timeNeeded } from '@/lib/board'
import { formatDay, formatDue, formatMinutes } from '@/lib/format'
import { useSettings } from '@/lib/items'
import { markerText } from '@/lib/lists'
import { headsUpTitle } from '@/lib/plan'
import { showsPriority } from '@/lib/priority'
import { conditionLabel, ruleAt } from '@/lib/rules'
import { attention } from '@/lib/styles'
import { cn } from '@/lib/utils'
import { PriorityChip } from './priority-chip'
import { TagChips } from './tag-chips'

/** The column names in the "Move to" menu (focus work is part of today). */
const MENU_LABELS: Record<BoardColumn, string> = {
  ...COLUMN_LABELS,
  focus: 'Today · focus work',
}

/**
 * "Move to": every column with its number key (1–9). The card's own column is there, disabled,
 * so the numbers are always the same.
 */
export function MoveMenu({
  column,
  title,
  onMove,
  open,
  onOpenChange,
  trigger,
  tomorrowLabel,
}: {
  column: BoardColumn
  title: string
  onMove: (column: BoardColumn) => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  trigger: ReactElement
  /** "Tomorrow · Thu 22 Oct": tomorrow is the next working day. */
  tomorrowLabel?: string
}) {
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger render={trigger} />
      <DropdownMenuContent
        align="end"
        className="w-60"
        aria-label={`Move "${title}" to`}
        onKeyDown={(event) => {
          const target = boardColumns[Number(event.key) - 1]
          if (!target || event.metaKey || event.ctrlKey || event.altKey) return
          event.preventDefault()
          if (target === column) return
          onOpenChange?.(false)
          onMove(target)
        }}
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel>Move to</DropdownMenuLabel>
          {boardColumns.map((target, index) => (
            <DropdownMenuItem
              key={target}
              disabled={target === column}
              onClick={() => onMove(target)}
              data-testid="move-to"
              data-column={target}
            >
              {target === 'tomorrow' && tomorrowLabel ? tomorrowLabel : MENU_LABELS[target]}
              <DropdownMenuShortcut>{index + 1}</DropdownMenuShortcut>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export type CardFaceProps = {
  card: BoardCard
  /** The user's personal tags (their chips carry a house). */
  personalTags?: readonly string[]
  column: BoardColumn
  line: CardLine | null
  selected: boolean
  timeZone: string
  thisYear: number
  /** The copy that follows the pointer while dragging. */
  overlay?: boolean
  /** The card stays in place, faded, while its copy is dragged. */
  dragging?: boolean
  onSelect?: () => void
  onOpen?: () => void
  onMove?: (column: BoardColumn) => void
  tomorrowLabel?: string
}

/**
 * A card: its title, who and when, the line from hugo's plan (whether it fits, the time today,
 * when hugo does it), and what to tell someone. Click selects; clicking the selected card (or
 * Enter) opens the details. Snoozed cards are faded.
 */
export function CardFace({
  card,
  column,
  line,
  selected,
  timeZone,
  thisYear,
  overlay = false,
  dragging = false,
  onSelect,
  onOpen,
  onMove,
  tomorrowLabel,
  personalTags,
}: CardFaceProps) {
  const { item } = card
  const settings = useSettings()
  const priorityRule = ruleAt(settings.data?.rules, item.priorityRuleIndex)
  const meta: string[] = []
  // Who waits: "I owe Nina", "Waiting on Marco"; a task's person.
  const marker = markerText(item)
  if (marker) meta.push(marker)
  if (item.dueAt && item.kind !== 'waiting' && column !== 'done') {
    meta.push(`Due ${formatDue(item.dueAt, item.dueAllDay, timeZone)}`)
  }
  if ((item.kind === 'task' || item.kind === 'owe') && column !== 'done') {
    // "≈": hugo's default for the type; the user's own estimate shows as is.
    const minutes = formatMinutes(timeNeeded(item, settings.data?.rules.estimateMinutes))
    meta.push(item.estimateMinutes === null ? `≈ ${minutes}` : minutes)
  }
  const headsUp = item.headsUp
  return (
    <div
      className={cn(
        'group/card relative flex flex-col gap-1.5 rounded-lg border bg-card p-2.5 text-card-foreground shadow-xs transition-[opacity,box-shadow]',
        selected && 'border-ring/60 ring-2 ring-ring/40',
        item.status === 'snoozed' && 'opacity-60',
        dragging && 'opacity-30',
        overlay && 'rotate-1 cursor-grabbing shadow-lg ring-2 ring-ring/30',
      )}
    >
      <div className="flex items-start gap-1">
        <button
          type="button"
          data-card-open
          aria-current={selected || undefined}
          onClick={() => (selected ? onOpen?.() : onSelect?.())}
          onDoubleClick={() => onOpen?.()}
          // Tab to a card selects it: the card's ring shows where the keyboard is (a mouse
          // click selects on its own, and a second click opens).
          onFocus={(event) => {
            if (!selected && event.currentTarget.matches(':focus-visible')) onSelect?.()
          }}
          className="flex min-w-0 flex-1 flex-col gap-0.5 text-left outline-none"
        >
          <span
            className={cn(
              'line-clamp-3 text-sm font-medium break-words',
              column === 'done' && 'text-muted-foreground line-through',
            )}
          >
            {item.title}
          </span>
          {meta.length > 0 ? (
            <span className="truncate text-xs text-muted-foreground">{meta.join(' · ')}</span>
          ) : null}
        </button>
        {onMove && !overlay ? (
          <MoveMenu
            column={column}
            title={item.title}
            onMove={onMove}
            tomorrowLabel={tomorrowLabel}
            trigger={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Move "${item.title}"`}
                data-testid="card-menu"
                className="-mt-1 -mr-1 shrink-0 text-muted-foreground md:opacity-0 md:group-hover/card:opacity-100 md:group-focus-within/card:opacity-100 md:data-popup-open:opacity-100"
              >
                <EllipsisIcon aria-hidden="true" />
              </Button>
            }
          />
        ) : null}
      </div>
      {line ? (
        <p
          data-testid="card-line"
          data-tone={line.tone}
          className={cn(
            'text-xs',
            line.tone === 'attention'
              ? 'font-medium text-amber-800 dark:text-amber-300'
              : 'text-muted-foreground',
          )}
        >
          {line.text}
        </p>
      ) : null}
      {showsPriority(item.priority) || item.tags.length > 0 ? (
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          <PriorityChip
            priority={item.priority}
            rule={priorityRule ? conditionLabel(priorityRule.when) : null}
          />
          <TagChips tags={item.tags} personalTags={personalTags} />
        </div>
      ) : null}
      {card.carriedFrom || headsUp ? (
        <div className="flex flex-wrap gap-1">
          {card.carriedFrom ? (
            <Badge
              variant="outline"
              className="font-normal text-muted-foreground"
              data-testid="carried-from"
              title="Planned for an earlier day and not done yet: it is today's now"
            >
              <HistoryIcon aria-hidden="true" />
              From {formatDay(card.carriedFrom, thisYear)}
            </Badge>
          ) : null}
          {headsUp ? (
            <Badge
              variant="outline"
              className={attention.badge}
              data-testid="card-headsup"
              title="Open the card to send or copy the text"
            >
              {headsUp.kind === 'waiting_followup' ? (
                <BellRingIcon aria-hidden="true" />
              ) : (
                <MegaphoneIcon aria-hidden="true" />
              )}
              {headsUpTitle(headsUp)}
            </Badge>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * A card on the board: dragged with the mouse, or by a long press on touch screens; a drop
 * target too (a card dropped on it goes before or after it).
 */
export function BoardCardItem(props: CardFaceProps) {
  const { card, column } = props
  const id = card.item.id
  const drag = useDraggable({ id, data: { column } })
  const drop = useDroppable({ id: `card:${id}`, data: { column, cardId: id } })
  const { setNodeRef: setDragRef } = drag
  const { setNodeRef: setDropRef } = drop
  const ref = useCallback(
    (node: HTMLLIElement | null) => {
      setDragRef(node)
      setDropRef(node)
    },
    [setDragRef, setDropRef],
  )
  return (
    <li
      ref={ref}
      {...drag.listeners}
      data-card-id={id}
      data-item-id={id}
      data-column={column}
      data-committed={card.committed}
      data-fits={card.fits === null ? undefined : card.fits}
      data-selected={props.selected}
      // A long press picks the card up: no text selection or iOS callout in its way.
      className="touch-manipulation select-none [-webkit-touch-callout:none]"
    >
      <CardFace {...props} dragging={drag.isDragging} />
    </li>
  )
}
